
import React, { Component } from 'react';
import {
  daysInMonth, weekDayInMonth, getDateForCalendar, getMonth, getDateForFilter,
} from '../utils/common';
import SVGComponent from './SVG/SVGComponent';
import { SVG_ICONS } from '../constants';

/**
 * @module Calendar
 */
/**
 * @typedef {object} props
 * @property {string} className
 * @property {boolean} sub
 * @property {string} field
 * @property {object} filter
 * @property {object} defaultFilter
 * @property {boolean} actions
 * @property {Time} inactivePreviousDates Use when date need to be inactive
 * before some date.
 * @property {Function} changeFilter
 */
export default class Calendar extends Component {
  constructor(props) {
    super(props);
    /**
     * @member {object}
     * @property {Time} selectedDate
     * @property {boolean} isOpen
     * @property {Time} date
     */
    const defaultDate = props.filter[props.field];
    const selectedDate = defaultDate ? new Date(defaultDate) : new Date();

    this.state = {
      month: new Date(selectedDate.getTime()),
      selectedDate,
      isOpen: false,
      date: defaultDate ? new Date(defaultDate) : null,
    };

    /**
     * @member {Array}
     */
    this.inactiveDatesBefore = [];
    /**
     * @member {Array}
     */
    this.activeDates = [];
    /**
     * @member {Array}
     */
    this.inactiveDatesAfter = [];

    // COMPONENT WILL MOUNT
    this.setDataForOneMonth(this.state.selectedDate);
  }

  /**
   * Update state on clear filter
   *
   * @param {object} prevProps
   * @returns {void}
   */
  componentDidUpdate(prevProps) {
    if (this.props.filter[this.props.field] !== prevProps.filter[prevProps.field]) {
      const defaultDate = this.props.filter[this.props.field];
      const selectedDate = defaultDate ? new Date(defaultDate) : new Date();

      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        month: new Date(selectedDate.getTime()),
        selectedDate,
        isOpen: false,
        date: defaultDate ? new Date(defaultDate) : null,
      });
    }
  }

  /**
   * Toggle calendar
   *
   * @function
   * @returns {void}
   */
  toggleCalendarPopup = () => {
    if (!this.state.isOpen) {
      this.setState({
        isOpen: true,
      });
    } else {
      this.cancel();
    }
  };

  /**
   * Set previous month
   *
   * @function
   * @returns {void}
   */
  setPreviousMonth = () => {
    const month = this.state.month;
    if (this.props.inactivePreviousDates
      && this.props.inactivePreviousDates.getMonth() === month.getMonth()) {
      return;
    }
    month.setMonth(month.getMonth() - 1, 1);
    this.setDataForOneMonth(month);
    this.setState({
      month,
    });
  };

  /**
   * Set next month
   *
   * @function
   * @returns {void}
   */
  setNextMonth = () => {
    const month = this.state.month;
    month.setMonth(month.getMonth() + 1, 1);
    this.setDataForOneMonth(month);
    this.setState({
      month,
    });
  };

  /**
   * Change day
   *
   * @function
   * @param {number} day
   * @returns {void}
   */
  setDay = (day) => {
    const date = this.state.selectedDate;
    date.setFullYear(this.state.month.getFullYear(), this.state.month.getMonth(), day);
    this.setState({
      selectedDate: date,
      month: new Date(date.getTime()),
    }, () => {
      if (!this.props.actions) {
        this.applay();
      }
    });
  };

  setPreviousDay = () => {
    const date = this.state.selectedDate;
    date.setDate(date.getDate() - 1);
    this.setState({
      selectedDate: date,
      month: new Date(date.getTime()),
    }, () => {
      this.applay();
    });
  };

  setNextDay = () => {
    const date = this.state.selectedDate;
    date.setDate(date.getDate() + 1);
    this.setState({
      selectedDate: date,
      month: new Date(date.getTime()),
    }, () => {
      this.applay();
    });
  };

  /**
   * Set default filter
   *
   * @function
   * @returns {void}
   */
  // eslint-disable-next-line react/no-unused-class-component-methods
  reset = () => {
    let date;
    if (this.props.defaultFilter && this.props.defaultFilter[this.props.field]) {
      date = new Date(this.props.defaultFilter[this.props.field]);
      this.props.changeFilter(getDateForFilter(date), this.props.field);
    }
    const selectedDate = date ? new Date(date.getTime()) : new Date();
    this.setState({
      isOpen: false,
      date,
      selectedDate,
      month: new Date(selectedDate.getTime()),
    });
  };

  /**
   * Discard changes
   *
   * @function
   * @returns {void}
   */
  cancel = () => {
    const selectedDate = this.state.date ? new Date(this.state.date.getTime()) : new Date();

    this.setState({
      isOpen: false,
      selectedDate,
      month: new Date(selectedDate.getTime()),
    });
  };

  /**
   * Change date
   *
   * @function
   * @returns {void}
   */
  applay = () => {
    this.props.changeFilter(getDateForFilter(this.state.selectedDate), this.props.field);
    this.setState({
      isOpen: false,
      date: this.state.selectedDate ? new Date(this.state.selectedDate.getTime()) : null,
    });
  };

  /**
   * Calculate month view
   *
   * @function
   * @param {Time} date
   */
  setDataForOneMonth = (date) => {
    const copiedDate = new Date(date.getTime());

    let numOfDays = daysInMonth(copiedDate.getMonth() + 1, copiedDate.getFullYear());
    const startWith = weekDayInMonth(copiedDate.getMonth(), copiedDate.getFullYear());
    copiedDate.setFullYear(copiedDate.getFullYear(), copiedDate.getMonth() - 1, 1);
    let numOfDaysPreviousMonth = daysInMonth(copiedDate.getMonth() + 1, copiedDate.getFullYear());
    numOfDaysPreviousMonth -= startWith;
    this.inactiveDatesBefore = Array(startWith).fill().map(() => {
      numOfDaysPreviousMonth += 1;
      return numOfDaysPreviousMonth;
    });
    let counter = 0;
    if (this.props.inactivePreviousDates
      && this.props.inactivePreviousDates.getMonth() === this.state.month.getMonth()) {
      const numOfInvalidDates = this.props.inactivePreviousDates.getDate() - 1;
      Array(numOfInvalidDates).fill().map(() => {
        counter += 1;
        this.inactiveDatesBefore.push(counter);
      });
      numOfDays -= numOfInvalidDates;
    }
    this.activeDates = Array(numOfDays).fill().map(() => {
      counter += 1;
      return counter;
    });
    let i = 0;
    this.inactiveDatesAfter = Array(42 - counter - startWith).fill().map(() => {
      i += 1;
      return i;
    });
  };

  /**
   * Parse date objects
   *
   * @function
   * @returns {object}
   */
  getCalendarData = () => {
    const fullDate = getDateForCalendar(this.state.selectedDate);
    let date = getDateForCalendar(this.state.date);
    date = date.slice(0, -4) + date.slice(date.length - 2, date.length);
    const month = getMonth(this.state.month);
    const currentDay = this.state.month.getMonth() === this.state.selectedDate.getMonth()
      ? this.state.selectedDate.getDate() : -1;
    return {
      currentDay, date, month, fullDate,
    };
  };

  /**
   * Render
   *
   * @returns {view}
   */
  render() {
    const data = this.getCalendarData();
    return (
      <div className={this.props.className}>
        <span className="prev-arrow" onClick={this.setPreviousDay} />
        <span className="next-arrow" onClick={this.setNextDay} />
        <div className={`filter__field-input hasCalendar  ${this.state.isOpen && 'open'}`} onClick={this.toggleCalendarPopup}>
          <div className="mask" />
          <div className="filter__field-input-toggle">
            <span className="filter__field-input-date">{data.date}</span>
            <span className={`calendar-icon ${this.props.sub && 'green'}`} onClick={this.toggleCalendarPopup} />
          </div>
        </div>
        {this.state.isOpen && (
          <div className={`calendar ${this.props.sub && 'sub bg-grey'}`}>
            <div className="calendar__head">
              <span>Select Date</span>
              <SVGComponent
                className={`icon-xxs calendar__popup-close${this.props.sub ? '-sub' : ''}`}
                src={`${SVG_ICONS.utility}#close`}
                onClick={this.toggleCalendarPopup}
              />
            </div>
            <hr className="spacer-line m-t-10 m-b-10" />
            <div className="calendar__display">
              <span className="calendar__display-prev" onClick={this.setPreviousMonth} />
              <div className="calendar__display-date">{data.month}</div>
              <span className="calendar__display-next" onClick={this.setNextMonth} />
            </div>
            <div className="calendar__table m-t-10">
              <div className="calendar__cell calendar__cell--day">S</div>
              <div className="calendar__cell calendar__cell--day">M</div>
              <div className="calendar__cell calendar__cell--day">T</div>
              <div className="calendar__cell calendar__cell--day">W</div>
              <div className="calendar__cell calendar__cell--day">T</div>
              <div className="calendar__cell calendar__cell--day">F</div>
              <div className="calendar__cell calendar__cell--day">S</div>
              {
                this.inactiveDatesBefore.map((day) => (
                  <div className="calendar__cell calendar__cell--disabled" key={day}><span>{day}</span></div>
                ))
              }
              {
                this.activeDates.map((day) => (
                  <div
                    key={day}
                    className={`calendar__cell ${data.currentDay === day ? 'active' : ''}`}
                    onClick={this.setDay.bind(this, day)}
                  >
                    <span>{day}</span>
                  </div>
                ))
              }
              {
                this.inactiveDatesAfter.map((day) => (
                  <div className="calendar__cell calendar__cell--disabled" key={day}><span>{day}</span></div>
                ))
              }
            </div>
            <hr className="spacer-line grey m-t-10 m-b-10" />
            {
              this.props.actions
              && (
                <div className="calendar__btn-wrap">
                  {/* <button
                type="button"
                className="btn btn-secondary mr-auto"
                onClick={this.reset}
              >
                Reset
              </button> */}
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={this.cancel}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={this.applay}
                    className="btn btn-primary m-l-10"
                  >
                    Apply
                  </button>
                </div>
              )
            }
          </div>
        )}
      </div>
    );
  }
}
